import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  getCareerApplications, updateCareerApplicationStatus,
  deleteCareerApplication, updateCareerApplicationNotes,
  updateCareerApplicationInfo, getCareerPositions,
} from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, Search, Download, Loader2, User, MapPin, Briefcase,
  Trash2, MessageSquare, Save, X, Pencil, ArrowLeft,
} from "lucide-react";

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-700" },
  { value: "reviewed", label: "Reviewed", color: "bg-yellow-100 text-yellow-700" },
  { value: "interviewed", label: "Interviewed", color: "bg-purple-100 text-purple-700" },
  { value: "hired", label: "Hired", color: "bg-green-100 text-green-700" },
  { value: "rejected", label: "Rejected", color: "bg-red-100 text-red-700" },
];

const POSITION_TYPE_LABELS = {
  sales_consultant: "Sales Consultant",
  sales_rep_pt: "Sales Consultant (Part-Time)",
  assistant_manager: "Assistant Manager",
  store_manager: "Store Manager",
};

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminResumes({ embedded = false }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [notesDialog, setNotesDialog] = useState(false);
  const [notesApp, setNotesApp] = useState(null);
  const [notesText, setNotesText] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editApp, setEditApp] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [positions, setPositions] = useState([]);
  const [posSearch, setPosSearch] = useState("");

  const loadData = async () => {
    try {
      const [apps, pos] = await Promise.all([getCareerApplications(), getCareerPositions()]);
      setApplications(apps);
      setPositions(pos);
    } catch (e) {
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleStatusChange = async (appId, newStatus) => {
    try {
      await updateCareerApplicationStatus(appId, newStatus);
      toast.success("Status updated");
      loadData();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (appId) => {
    if (!window.confirm("Delete this application? This cannot be undone.")) return;
    try {
      await deleteCareerApplication(appId);
      toast.success("Application deleted");
      loadData();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const openNotes = (app) => {
    setNotesApp(app);
    setNotesText(app.notes || "");
    setNotesDialog(true);
  };

  const saveNotes = async () => {
    if (!notesApp) return;
    setSavingNotes(true);
    try {
      await updateCareerApplicationNotes(notesApp.id, notesText);
      toast.success("Notes saved");
      setNotesDialog(false);
      loadData();
    } catch (e) {
      toast.error("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const openEdit = (app) => {
    setEditApp(app);
    setEditForm({ first_name: app.first_name, last_name: app.last_name, email: app.email, phone: app.phone, position_id: app.position_id || "", street: app.street || "", city: app.city || "", state: app.state || "", zip_code: app.zip_code || "" });
    setPosSearch("");
    setEditDialog(true);
  };

  const saveEdit = async () => {
    if (!editApp) return;
    setSavingEdit(true);
    try {
      await updateCareerApplicationInfo(editApp.id, editForm);
      toast.success("Applicant info updated");
      setEditDialog(false);
      loadData();
    } catch (e) {
      toast.error("Failed to update");
    } finally {
      setSavingEdit(false);
    }
  };

  const filtered = applications.filter((app) => {
    const matchStatus = filterStatus === "all" || app.status === filterStatus;
    const matchType = filterType === "all" || app.position_type === filterType;
    const matchSearch = !search ||
      `${app.first_name} ${app.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
      app.email.toLowerCase().includes(search.toLowerCase()) ||
      (app.applicant_id || "").toLowerCase().includes(search.toLowerCase()) ||
      (app.position_title || "").toLowerCase().includes(search.toLowerCase()) ||
      (app.location_name || "").toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchType && matchSearch;
  });

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find((o) => o.value === status) || STATUS_OPTIONS[0];
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>{opt.label}</span>;
  };

  const statusCounts = STATUS_OPTIONS.map((s) => ({
    ...s,
    count: applications.filter((a) => a.status === s.value).length,
  }));

  return (
    <AdminLayout title="Career Applications" breadcrumb={embedded ? "Reports / Resumes" : "Resumes"}>
      <div className="space-y-6" data-testid="admin-resumes">
        {embedded && (
          <Link to="/admin/reports" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-teal font-body" data-testid="resumes-back-to-reports">
            <ArrowLeft className="w-4 h-4" /> Back to Reports
          </Link>
        )}
        <div className="bg-navy rounded-xl p-6 text-white">
          <h2 className="font-heading font-bold text-2xl mb-2 flex items-center gap-2">
            <FileText className="w-6 h-6" /> Career Applications & Resumes
          </h2>
          <p className="text-slate-300 font-body">
            {applications.length} total applications — Review, manage notes, download resumes, and track hiring pipeline.
          </p>
        </div>

        {/* Status summary */}
        <div className="flex flex-wrap gap-3">
          {statusCounts.map((s) => (
            <button
              key={s.value}
              onClick={() => setFilterStatus(filterStatus === s.value ? "all" : s.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                filterStatus === s.value ? "border-teal bg-teal/10 text-teal" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}
              data-testid={`status-filter-${s.value}`}
            >
              {s.label}: {s.count}
            </button>
          ))}
        </div>

        {/* Search + Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, position..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="resume-search"
            />
          </div>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white" data-testid="resume-filter-type">
            <option value="all">All Positions</option>
            <option value="sales_consultant">Sales Consultant</option>
            <option value="sales_rep_pt">Sales Consultant (Part-Time)</option>
            <option value="assistant_manager">Assistant Manager</option>
            <option value="store_manager">Store Manager</option>
          </select>
          <span className="text-xs text-slate-400">{filtered.length} results</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-navy text-lg mb-2">No Applications</h3>
            <p className="text-slate-500 font-body">
              {search || filterStatus !== "all" ? "No results match your filters." : "No career applications have been submitted yet."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((app) => (
              <Card key={app.id} className="border-slate-200 hover:border-slate-300 transition-colors" data-testid={`resume-card-${app.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {getStatusBadge(app.status)}
                        <span className="px-2 py-0.5 bg-teal/10 text-teal text-xs font-medium rounded-full">
                          {POSITION_TYPE_LABELS[app.position_type] || app.position_type}
                        </span>
                      </div>
                      <h3 className="font-heading font-semibold text-navy text-base flex items-center gap-1">
                        <User className="w-4 h-4 text-slate-400" />
                        {app.first_name} {app.last_name}
                        {app.applicant_id && <span className="text-xs font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded ml-1">{app.applicant_id}</span>}
                      </h3>
                      <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500 font-body">
                        <span>{app.email}</span>
                        <span>{app.phone}</span>
                        {app.city && <span>{app.street ? `${app.street}, ` : ""}{app.city}, {app.state} {app.zip_code}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400 font-body">
                        <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{app.position_title}</span>
                        {app.location_name && (
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{app.location_name} — {app.location_city}, {app.location_state}</span>
                        )}
                        <span>{new Date(app.created_at).toLocaleDateString()}</span>
                      </div>
                      {app.nearby_locations?.length > 0 && (
                        <div className="mt-1.5 flex items-start gap-1 text-xs">
                          <MapPin className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                          <span className="text-amber-600">
                            Within 15 mi: {app.nearby_locations.map((nl) => `${nl.name} (${nl.city}, ${nl.state})`).join(" · ")}
                          </span>
                        </div>
                      )}
                      {app.cover_letter && (
                        <p className="text-xs text-slate-400 mt-1 line-clamp-1 italic">"{app.cover_letter}"</p>
                      )}
                      {app.notes && (
                        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" /> {app.notes.length > 80 ? app.notes.slice(0, 80) + "..." : app.notes}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                      {app.resume_url && (
                        <a
                          href={`${API_URL}${app.resume_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal/10 text-teal text-xs font-medium rounded-lg hover:bg-teal/20 transition-colors"
                          data-testid={`download-resume-${app.id}`}
                        >
                          <Download className="w-3.5 h-3.5" /> Resume
                        </a>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => openEdit(app)}
                        data-testid={`edit-app-${app.id}`}
                      >
                        <Pencil className="w-3 h-3" /> Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => openNotes(app)}
                        data-testid={`notes-btn-${app.id}`}
                      >
                        <MessageSquare className="w-3 h-3" /> Notes
                      </Button>
                      <select
                        value={app.status}
                        onChange={(e) => handleStatusChange(app.id, e.target.value)}
                        className="border border-slate-200 rounded-lg px-2 py-1 text-xs bg-white"
                        data-testid={`status-select-${app.id}`}
                      >
                        {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(app.id)}
                        data-testid={`delete-app-${app.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Notes Dialog */}
        <Dialog open={notesDialog} onOpenChange={setNotesDialog}>
          <DialogContent className="max-w-lg" data-testid="notes-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Notes — {notesApp?.first_name} {notesApp?.last_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <p className="text-xs text-slate-500 font-body">
                {notesApp?.position_title} at {notesApp?.location_name}
              </p>
              <Textarea
                value={notesText}
                onChange={(e) => setNotesText(e.target.value)}
                placeholder="Add notes about this applicant (interview feedback, qualifications, follow-up actions...)"
                rows={6}
                data-testid="notes-textarea"
              />
              <div className="flex gap-2">
                <Button onClick={saveNotes} disabled={savingNotes} className="bg-teal hover:bg-teal/90 flex-1" data-testid="save-notes-btn">
                  {savingNotes ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Notes
                </Button>
                <Button variant="outline" onClick={() => setNotesDialog(false)}>Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* Edit Applicant Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="max-w-lg" data-testid="edit-app-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Pencil className="w-5 h-5" />
                Edit Applicant / Reassign Store
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-slate-600 mb-1 block">First Name</label>
                  <Input value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} data-testid="edit-first-name" />
                </div>
                <div>
                  <label className="text-sm text-slate-600 mb-1 block">Last Name</label>
                  <Input value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} data-testid="edit-last-name" />
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Email</label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-email" />
              </div>
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Phone</label>
                <Input type="tel" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-phone" />
              </div>
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Street Address</label>
                <Input value={editForm.street} onChange={(e) => setEditForm({ ...editForm, street: e.target.value })} data-testid="edit-street" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-sm text-slate-600 mb-1 block">City</label>
                  <Input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} data-testid="edit-city" />
                </div>
                <div>
                  <label className="text-sm text-slate-600 mb-1 block">State</label>
                  <Input value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} maxLength={2} data-testid="edit-state" />
                </div>
                <div>
                  <label className="text-sm text-slate-600 mb-1 block">Zip</label>
                  <Input value={editForm.zip_code} onChange={(e) => setEditForm({ ...editForm, zip_code: e.target.value })} data-testid="edit-zip" />
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-600 mb-1 block flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> Reassign to Different Position / Store
                </label>
                <Input
                  placeholder="Type to search stores or positions..."
                  value={posSearch}
                  onChange={(e) => setPosSearch(e.target.value)}
                  className="mb-2"
                  data-testid="edit-position-search"
                />
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-md">
                  <button
                    type="button"
                    onClick={() => { setEditForm({ ...editForm, position_id: "" }); setPosSearch(""); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${!editForm.position_id ? "bg-teal/10 text-teal font-medium" : "text-slate-600"}`}
                    data-testid="edit-position-keep"
                  >
                    — Keep Current —
                  </button>
                  {(() => {
                    const q = posSearch.toLowerCase();
                    const grouped = {};
                    positions.forEach((p) => {
                      const loc = p.location_name ? `${p.location_name} — ${p.location_city}, ${p.location_state}` : "Unknown Location";
                      const matchesSearch = !q || loc.toLowerCase().includes(q) || p.title.toLowerCase().includes(q) || (p.position_type || "").toLowerCase().includes(q);
                      if (matchesSearch) {
                        if (!grouped[loc]) grouped[loc] = [];
                        grouped[loc].push(p);
                      }
                    });
                    const entries = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
                    if (entries.length === 0 && q) return <div className="px-3 py-3 text-xs text-slate-400 text-center">No matching stores</div>;
                    return entries.map(([loc, pos]) => (
                      <div key={loc}>
                        <div className="px-3 py-1.5 bg-slate-50 text-xs font-semibold text-slate-500 sticky top-0">{loc}</div>
                        {pos.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => { setEditForm({ ...editForm, position_id: p.id }); setPosSearch(""); }}
                            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-teal/5 ${editForm.position_id === p.id ? "bg-teal/10 text-teal font-medium" : "text-slate-700"}`}
                            data-testid={`pos-option-${p.id}`}
                          >
                            {p.title} <span className="text-slate-400 text-xs">({POSITION_TYPE_LABELS[p.position_type] || p.position_type})</span>
                          </button>
                        ))}
                      </div>
                    ));
                  })()}
                </div>
                {editForm.position_id && editForm.position_id !== editApp?.position_id && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Applicant will be rerouted to a new store/position
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={saveEdit} disabled={savingEdit} className="bg-teal hover:bg-teal/90 flex-1" data-testid="save-edit-btn">
                  {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
                <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
